/**
 * 13.11 (migration 0263; D-10, D-11, D-12, D-28) — an operator company's MQTT gateways, on the
 * operator page beside its API keys. Paelode adds, rotates and revokes them here (a producer never
 * provisions its own). Each gateway is an AWS IoT thing with its own certificate; it connects with
 * its thing name as the client id and publishes to its own topic.
 *
 * The certificate and the private key are shown once, right after adding or rotating, and never
 * again: only the certificate's id is stored.
 */
import * as React from 'react';
import { toast } from 'sonner';
import type {
  MqttGatewayBundle,
  MqttGatewayView,
} from '@lodex-pro/shared/validation/mqtt-gateways';
import { Button } from 'src/components/ui/button';
import { FormRow, Pill, fieldStyle, subText } from '../../ui/bits';
import {
  MQTT_STATUS_LABELS,
  mqttGatewayError,
  useAddMqttGateway,
  useFinishMqttRotation,
  useMqttGateways,
  useRefreshMqttGateways,
  useRevokeMqttGateway,
  useRotateMqttGateway,
} from './mqtt-gateway-hooks';
import { formatNumber } from '@lodex-pro/shared/number-format';

const cardStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-bg-secondary)',
  border: '1px solid var(--color-border-primary)',
  borderRadius: '8px',
  padding: '20px',
  marginBottom: '16px',
};
const cell: React.CSSProperties = {
  padding: '6px',
  borderTop: '1px solid var(--color-border-primary)',
  verticalAlign: 'top',
};
const pre: React.CSSProperties = {
  display: 'block',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-all',
  fontSize: '12px',
  maxHeight: '140px',
  overflow: 'auto',
  margin: '4px 0',
};

/**
 * A gateway still PENDING after STUCK_AFTER_MS never finished provisioning (the API stopped
 * mid-way). It still counts toward the plan's limit until revoked (review L3).
 */
const STUCK_AFTER_MS = 10 * 60 * 1000;
export function isStuck(g: MqttGatewayView, now: number = Date.now()): boolean {
  return g.status === 'PENDING' && now - Date.parse(g.createdAt) > STUCK_AFTER_MS;
}

export const REVOKE_WARNING = "The gateway disconnects within a few minutes and can't reconnect.";

/** Copy and download-as-text for one secret file. */
function SecretFile({
  title,
  text,
  fileName,
  testId,
}: {
  title: string;
  text: string;
  fileName: string;
  testId: string;
}): React.JSX.Element {
  const download = (): void => {
    if (typeof URL.createObjectURL !== 'function') return;
    const url = URL.createObjectURL(new Blob([text], { type: 'text/plain' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div style={{ marginBottom: '8px' }}>
      <div style={{ fontWeight: 600, fontSize: '13px' }}>{title}</div>
      <code data-testid={testId} style={pre}>
        {text}
      </code>
      <div style={{ display: 'flex', gap: '8px' }}>
        <Button
          type="button"
          variant="outline"
          onClick={(): void => {
            void navigator.clipboard?.writeText(text);
            toast.success('Copied');
          }}
        >
          Copy
        </Button>
        <Button type="button" variant="outline" onClick={download}>
          Download {fileName}
        </Button>
      </div>
    </div>
  );
}

/** What the gateway needs, once. */
function BundleOnce({
  bundle,
  rotation,
  onDone,
}: {
  bundle: MqttGatewayBundle;
  rotation: boolean;
  onDone: () => void;
}): React.JSX.Element {
  return (
    <div
      role="alert"
      data-testid="mqtt-bundle"
      style={{
        border: '1px solid var(--color-border-primary)',
        borderRadius: '6px',
        padding: '12px',
        margin: '12px 0',
      }}
    >
      <p style={{ margin: '0 0 6px', fontWeight: 600 }}>
        {rotation ? `A new certificate for ${bundle.label}.` : `${bundle.label} is ready.`} The
        private key is shown only now. Store it on the gateway; we can’t show it again.
      </p>
      <dl style={{ margin: '0 0 8px', fontSize: '13px' }}>
        <dt>Endpoint</dt>
        <dd data-testid="mqtt-endpoint">
          {bundle.endpoint}, port {bundle.port}
        </dd>
        <dt>Client id</dt>
        <dd>{bundle.clientId}</dd>
        <dt>Topic</dt>
        <dd>{bundle.topic}</dd>
        <dt>Server certificate</dt>
        <dd>
          <a href={bundle.rootCaUrl} target="_blank" rel="noreferrer">
            {bundle.rootCaName}
          </a>
        </dd>
      </dl>
      <SecretFile
        title="Certificate"
        text={bundle.certificatePem}
        fileName="certificate.pem.crt"
        testId="mqtt-cert"
      />
      <SecretFile
        title="Private key"
        text={bundle.privateKey}
        fileName="private.pem.key"
        testId="mqtt-private-key"
      />
      {rotation && (
        <p style={{ ...subText, textTransform: 'none' }}>
          The old certificate keeps working until you finish the rotation. Finish it once the
          gateway connects with the new one.
        </p>
      )}
      <Button type="button" onClick={onDone}>
        Done
      </Button>
    </div>
  );
}

function GatewayActions({
  tenantId,
  g,
  onBundle,
  onChanged,
}: {
  tenantId: string;
  g: MqttGatewayView;
  /** The bundle, and how to drop the mutation's own copy of it once the reveal closes. */
  onBundle: (b: MqttGatewayBundle, forget: () => void) => void;
  onChanged: () => void;
}): React.JSX.Element | null {
  const revoke = useRevokeMqttGateway(tenantId, g.id);
  const rotate = useRotateMqttGateway(tenantId, g.id);
  const finish = useFinishMqttRotation(tenantId, g.id);
  if (g.status !== 'ACTIVE' && g.status !== 'PENDING') return null;
  return (
    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
      {g.status === 'ACTIVE' &&
        (g.rotating ? (
          <Button
            type="button"
            variant="outline"
            disabled={finish.isPending}
            onClick={(): void =>
              finish.mutate(
                {},
                {
                  onSuccess: () => {
                    toast.success(`${g.label}: the old certificate is off.`);
                    onChanged();
                  },
                  onError: (err) => toast.error(mqttGatewayError(err)),
                },
              )
            }
          >
            Finish rotation
          </Button>
        ) : (
          <Button
            type="button"
            variant="outline"
            disabled={rotate.isPending}
            onClick={(): void => {
              if (
                !window.confirm(
                  `Rotate ${g.label}? The old certificate keeps working until you finish.`,
                )
              ) {
                return;
              }
              rotate.mutate(
                {},
                {
                  onSuccess: (b) => onBundle(b, () => rotate.reset()),
                  onError: (err) => toast.error(mqttGatewayError(err)),
                },
              );
            }}
          >
            Rotate
          </Button>
        ))}
      <Button
        type="button"
        variant="outline"
        disabled={revoke.isPending}
        onClick={(): void => {
          if (!window.confirm(`Revoke ${g.label}? ${REVOKE_WARNING}`)) return;
          revoke.mutate(
            {},
            {
              onSuccess: () => {
                toast.success(`${g.label} revoked. ${REVOKE_WARNING}`);
                onChanged();
              },
              onError: (err) => toast.error(mqttGatewayError(err)),
            },
          );
        }}
      >
        Revoke
      </Button>
    </div>
  );
}

export function OperatorMqttGatewaysCard({ tenantId }: { tenantId: string }): React.JSX.Element {
  const { data, isLoading, isError } = useMqttGateways(tenantId);
  const refresh = useRefreshMqttGateways(tenantId);
  const add = useAddMqttGateway(tenantId);
  const [label, setLabel] = React.useState('');
  const [shown, setShown] = React.useState<{
    bundle: MqttGatewayBundle;
    rotation: boolean;
    /** Drops the mutation's cached copy, so the key is not kept in memory (review L1). */
    forget: () => void;
  } | null>(null);
  const [showRevoked, setShowRevoked] = React.useState(false);

  const gateways = (data?.gateways ?? []).filter(
    (g) => showRevoked || (g.status !== 'REVOKED' && g.status !== 'FAILED'),
  );
  const atLimit = data !== undefined && data.limit !== null && data.liveCount >= data.limit;
  const canAdd = data !== undefined && data.mqttEnabled && !atLimit;

  return (
    <section style={cardStyle} aria-label="MQTT gateways">
      <h2 style={{ margin: '0 0 4px', fontSize: '16px' }}>MQTT gateways</h2>
      <p style={{ ...subText, textTransform: 'none', margin: '0 0 8px' }}>
        A gateway at the producer’s site sends tank levels over MQTT with its own certificate. Only
        Paelode adds them.
      </p>
      {isLoading ? (
        <p style={subText}>Loading…</p>
      ) : isError || data === undefined ? (
        <p style={subText}>Could not load the gateways.</p>
      ) : (
        <>
          <p style={{ margin: '0 0 8px', fontSize: '14px' }}>
            {!data.mqttEnabled
              ? "OP_MQTT is not on this company's plan."
              : data.limit === null
                ? `${formatNumber(data.liveCount)} gateways · no limit`
                : `${formatNumber(data.liveCount)} of ${data.limit} gateways in use`}
          </p>
          {shown !== null && (
            <BundleOnce
              bundle={shown.bundle}
              rotation={shown.rotation}
              onDone={(): void => {
                shown.forget();
                setShown(null);
              }}
            />
          )}
          {gateways.length === 0 ? (
            <p style={{ ...subText, textTransform: 'none' }}>No gateways yet.</p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ textAlign: 'left' }}>
                  <th style={cell}>Label</th>
                  <th style={cell}>Status</th>
                  <th style={cell}>Client id</th>
                  <th style={cell}>Last seen</th>
                  <th style={cell} />
                </tr>
              </thead>
              <tbody>
                {gateways.map((g) => (
                  <tr key={g.id} data-testid="mqtt-gateway-row">
                    <td style={cell}>{g.label}</td>
                    <td style={cell}>
                      {MQTT_STATUS_LABELS[g.status]}
                      {isStuck(g) && (
                        <div>
                          <Pill label="Stuck: revoke it to free the slot" tone="danger" />
                        </div>
                      )}
                      {g.rotating && (
                        <div>
                          <Pill label="Rotating" tone="warning" />
                        </div>
                      )}
                    </td>
                    <td style={cell}>{g.thingName}</td>
                    <td style={cell}>
                      {/* Local time, never UTC (DEC-004). */}
                      {g.lastSeenAt === null ? 'Never' : new Date(g.lastSeenAt).toLocaleString()}
                    </td>
                    <td style={cell}>
                      <GatewayActions
                        tenantId={tenantId}
                        g={g}
                        onBundle={(b, forget): void => {
                          setShown({ bundle: b, rotation: true, forget });
                          refresh();
                        }}
                        onChanged={refresh}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <label style={{ display: 'inline-flex', gap: '4px', margin: '8px 0', fontSize: '13px' }}>
            <input
              type="checkbox"
              checked={showRevoked}
              onChange={(e): void => setShowRevoked(e.target.checked)}
            />
            Show revoked
          </label>
          <div style={{ marginTop: '8px' }}>
            <FormRow label="New gateway label" htmlFor={`mqtt-label-${tenantId}`}>
              <input
                id={`mqtt-label-${tenantId}`}
                value={label}
                onChange={(e): void => setLabel(e.target.value)}
                placeholder="e.g. Lease 7 north battery"
                disabled={!canAdd}
                // Stored upper case (DEC-147, #1045): show it as it will be saved.
                style={{ ...fieldStyle, textTransform: 'uppercase' }}
              />
            </FormRow>
            {atLimit && (
              <p style={{ ...subText, textTransform: 'none' }}>
                All {data.limit} gateways this plan allows are in use. Revoke one, or raise the
                OP_MQTT limit in the Plan card.
              </p>
            )}
            <Button
              type="button"
              disabled={!canAdd || add.isPending || label.trim() === ''}
              onClick={(): void =>
                add.mutate(
                  { label: label.trim() },
                  {
                    onSuccess: (b) => {
                      setLabel('');
                      setShown({ bundle: b, rotation: false, forget: () => add.reset() });
                      refresh();
                    },
                    onError: (err) => toast.error(mqttGatewayError(err)),
                  },
                )
              }
            >
              Add gateway
            </Button>
          </div>
        </>
      )}
    </section>
  );
}
